import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../config/firebase";
import { collection, getDocs, doc, getDoc, addDoc, query, where } from "firebase/firestore";
import NavBar from "../components/navbar";
import Footer from "../components/footer";
import { useAuth } from "../config/AuthContext"; 
import "./EnrollPage.css";

const EnrollPage = () => {
  const { courseid } = useParams();
  const { user, isFetching } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [studentFirstName, setStudentFirstName] = useState('');
  const [studentMiddleName, setStudentMiddleName] = useState('');
  const [studentLastName, setStudentLastName] = useState('');
  const [studentBirthdate, setStudentBirthdate] = useState('');
  const [studentCost, setStudentCost] = useState(100); // Default cost
  const [showModal, setShowModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent multiple submissions

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const docRef = doc(db, "Course", courseid); 
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCourse(docSnap.data());
        } else {
          console.log("No such course!");
        }
      } catch (error) {
        console.error("Error fetching course:", error);
      }
    };
    fetchCourse();
  }, [courseid]);

  useEffect(() => {
    if (user) {
      const fetchStudents = async () => {
        try {
          const studentsRef = collection(db, `Users/${user.uid}/Students`);
          const studentSnapshot = await getDocs(studentsRef);
          const studentList = studentSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setStudents(studentList);
          setIsLoading(false);
        } catch (error) {
          console.error("Error fetching students:", error);
          setIsLoading(false);
        }
      };

      const fetchEnrollments = async () => {
        try {
          const enrollmentsRef = collection(db, `Users/${user.uid}/Enrollments`);
          const q = query(enrollmentsRef, where("courseId", "==", courseid));
          const enrollmentSnapshot = await getDocs(q);
          const enrolledList = enrollmentSnapshot.docs.map((doc) => doc.data().studentId);
          setEnrolledStudents(enrolledList); // Store enrolled student IDs
        } catch (error) {
          console.error("Error fetching enrollments:", error);
        }
      };

      fetchStudents();
      fetchEnrollments();
    }
  }, [user, courseid]);

  const handleSelectStudent = (student) => {
    // Check if the student is already enrolled
    if (enrolledStudents.includes(student.id)) {
      alert(`${student.firstName} ${student.lastName} has already enrolled in this course.`);
      return;
    }

    // Check if the student has completed the prerequisites
    checkPrerequisite(student);

    setSelectedStudents((prevSelected) => {
      const isSelected = prevSelected.some((s) => s.id === student.id);
      let updatedSelected = [];
      if (isSelected) {
        updatedSelected = prevSelected.filter((s) => s.id !== student.id);
      } else {
        updatedSelected = [...prevSelected, student];
      }
      const newTotalCost = updatedSelected.reduce((sum, s) => sum + course.cost, 0);
      setTotalCost(newTotalCost);
      return updatedSelected;
    });
  };

  const calculateAge = (birthdate) => {
    const birthDateObj = new Date(birthdate);
    const ageDifMs = Date.now() - birthDateObj.getTime();
    const ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevent submission if already submitting

    setIsSubmitting(true); // Set submitting state to true

    if (!studentFirstName || !studentLastName || !studentBirthdate) {
      alert("Please provide both full name and birthdate for the student.");
      setIsSubmitting(false);
      return;
    }

    const fullNameNormalized = `${studentFirstName.trim().toLowerCase()} ${studentMiddleName.trim().toLowerCase()} ${studentLastName.trim().toLowerCase()}`;
    const existingStudent = students.find(student => {
      const normalizedExistingName = `${student.firstName.trim().toLowerCase()} ${student.middleName.trim().toLowerCase()} ${student.lastName.trim().toLowerCase()}`;
      return normalizedExistingName === fullNameNormalized;
    });

    if (existingStudent) {
      alert("This student already exists.");
      setIsSubmitting(false);
      return;
    }

    try {
      const newStudent = {
        firstName: studentFirstName,
        middleName: studentMiddleName,
        lastName: studentLastName,
        birthdate: studentBirthdate,
        age: calculateAge(studentBirthdate),
        cost: studentCost,
        email: user.email, // Original user's email
        phoneNumber: user.phoneNumber || '', // Original user's phone number
        coursesCompleted: [] // Ensure this is initialized as an empty array if no courses completed
      };

      const docRef = await addDoc(collection(db, `Users/${user.uid}/Students`), newStudent);

      setStudents((prevStudents) => [
        ...prevStudents,
        { id: docRef.id, ...newStudent },
      ]);

      setStudentFirstName('');
      setStudentMiddleName('');
      setStudentLastName('');
      setStudentBirthdate('');
      setShowModal(false);

      alert("Student added successfully!");
    } catch (error) {
      console.error("Error adding student:", error);
      alert("Failed to add student.");
    } finally {
      setIsSubmitting(false); // Reset submitting state
    }
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleCheckout = () => {
    navigate(`/courses/${courseid}/confirmation`, {
      state: { selectedStudents, totalCost, course },
    });
  };
  const checkPrerequisite2 = (student) => {
    setShowContactModal(true); // Show the contact form modal
    alert(`To register ${student.firstName} ${student.lastName} please fill in the contact form`);

  };
  // Prerequisite check function
  const checkPrerequisite = (student) => {
    const courseNumber = parseInt(course?.number, 10);

    if (isNaN(courseNumber)) {
      console.error("Course number is not defined or invalid");
      return;
    }

    const completedCourses = student.coursesCompleted || [];
    console.log(completedCourses)
    const requiredCourses = Array.from({ length: courseNumber - 1 }, (_, index) => index + 1);

    const hasCompletedPrerequisites = requiredCourses.every(requiredNumber => 
      completedCourses.includes(requiredNumber)
    );

    if (!hasCompletedPrerequisites) {
      setShowContactModal(true); // Show the contact form modal
      alert(`${student.firstName} ${student.lastName} does not meet the prerequisites for this course.`);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (!contactName || !contactEmail) {
      alert("Please provide both your name and email.");
      return;
    }

    try {
      console.log('Contact Info Submitted:', { name: contactName, email: contactEmail });

      alert("Thank you! We will get back to you with an evaluation.");
      setShowContactModal(false); // Close the modal after submission
      setContactName('');
      setContactEmail('');
    } catch (error) {
      console.error("Error submitting contact info:", error);
      alert("Failed to submit contact information.");
    }
  };

  if (isFetching) return <p>Loading...</p>;
  if (isLoading) return <p>Loading students...</p>;
  if (!course) return <p>No course data available</p>;

  return (
    <div>
      <div className="enroll-page">
        <div className="enroll-content">
          <h1 className="page-title">{course?.Title} Enrollment</h1>
          <p className="instruction-message">
            Select all the students you want to enroll in {course?.Title}.
          </p>

          <div className="enroll-main">
            <div className="students-list">
              <h3>Students</h3>
              <table className="students-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Full Name</th>
                    <th>Age</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {students.length > 0 ? (
                    students.map((student) => (
                      <tr key={student.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedStudents.some((s) => s.id === student.id)}
                            onChange={() => handleSelectStudent(student)}
                          />
                        </td>
                        <td>{`${student.firstName} ${student.middleName} ${student.lastName}`}</td>
                        <td>{calculateAge(student.birthdate)}</td>
                        <td>{course?.cost}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4">No students found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="shopping-cart">
              <h3>Shopping Cart</h3>
              <ul>
                {selectedStudents.map((student) => (
                  <li key={student.id}>
                    {student.firstName} {student.middleName} {student.lastName} - ${course.cost}
                  </li>
                ))}
              </ul>
              <div className="subtotal">
                <strong>Subtotal: ${totalCost}</strong>
              </div>
            </div>
          </div>

          <button className="add-student-button" onClick={toggleModal}>
            Add Student
          </button>
          <button className="checkout-button" onClick={handleCheckout}>
            Proceed to Checkout
          </button>
          <p className="add-student-text">Need assistance? Contact our support team at support@courses.com</p>

          {showModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <button className="close-button" onClick={toggleModal}>X</button>
                <h3>Add a New Student</h3>
                <form onSubmit={handleAddStudent} className="add-student-form">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    value={studentFirstName}
                    onChange={(e) => setStudentFirstName(e.target.value)}
                    required
                  />
                  <label htmlFor="middleName">Middle Name</label>
                  <input
                    type="text"
                    id="middleName"
                    value={studentMiddleName}
                    onChange={(e) => setStudentMiddleName(e.target.value)}
                  />
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    value={studentLastName}
                    onChange={(e) => setStudentLastName(e.target.value)}
                    required
                  />
                  <label htmlFor="birthdate">Birthdate</label>
                  <input
                    type="date"
                    id="birthdate"
                    value={studentBirthdate}
                    onChange={(e) => setStudentBirthdate(e.target.value)}
                    required
                  />
                  <button type="submit" disabled={isSubmitting}>Add Student</button>
                </form>
              </div>
            </div>
          )}

          {showContactModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Contact Us for Evaluation</h3>
                <button className="close-button" onClick={() => setShowContactModal(false)}>X</button>
                <form onSubmit={handleContactSubmit} className="contact-form">
                  <label htmlFor="contactName">Student Name</label>
                  <input
                    type="text"
                    id="contactName"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    required
                  />
                  <label htmlFor="contactEmail">Your Email</label>
                  <input
                    type="email"
                    id="contactEmail"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    required
                  />
                  <p>We will get back to you with an evaluation.</p>
                  <button type="submit">Submit</button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>

      <NavBar />
      <Footer />
    </div>
  );
};

export default EnrollPage;
